import { SkyBadge, SkyButton, SkyLink, SkyText } from "@skypointcloud/skypoint-ui";
import { Link } from "wouter";
import { useEffect, useState } from "react";
import type { RunDetail } from "@shared/types/runs";
import { dbtSourceLine, dbtSourceOf } from "@shared/runs/dbtSource";
import { tableNoRows, tablesLoadedSentence } from "@shared/runs/noRows";
import { DONE_LABEL_MS } from "@/lib/feedback/NoticeRegion";
import { CopyIcon, LogsIcon } from "@/lib/icons";
import {
  formatCount,
  formatDuration,
  isNotStarted,
  isTerminalRun,
  loadHadNoDestination,
  LIVE_LOAD_ATTEMPT,
  logAnalyticsQuery,
  logAnalyticsUrl,
  RUN_KIND_LABELS,
  rowsLineParts,
  runStatusPresentation,
  startedText,
  cancelledByWording,
  type RowsLinePart,
  totalsFromRows,
  transformWhatRan,
} from "./runFormatting";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { absolutePath } from "@/lib/routes";
import { retryOfLabel, scopeTableCount, tablesWord } from "./tableRuns";
import { browserTimeZone, formatInstant, zoneCaption } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useElapsedSeconds } from "@/hooks/use-elapsed-seconds";

/**
 * The run at a glance (UX-5).
 *
 * The run id is copyable because it is the first thing support asks for, and
 * it is also the key of the Log Analytics query below it. That query is offered
 * as text whenever no workspace URL is configured for the deployment — which is
 * every build this branch produces — rather than as a link that goes nowhere: a
 * dead link costs a support round trip to discover it was never wired up.
 */

/**
 * The rows sentence (O1), shared by this card and the connection's last-run card. Only a failure
 * part is red, and its words say it failed, so colour is never the only signal.
 */
export function RowsLine({ parts, testId }: { parts: RowsLinePart[]; testId: string }) {
  return (
    <SkyText as="p" size={300} weight="semibold" block className="mb-4" testId={testId}>
      {parts.map((part, index) => (
        // The parts are an ordered sentence that is rebuilt whole; the position is the key.
        <span key={index}>
          {index > 0 ? <span className="font-normal text-muted-foreground"> · </span> : null}
          <span className={part.tone === "danger" ? "text-destructive" : undefined}>
            {part.text}
          </span>
        </span>
      ))}
    </SkyText>
  );
}

export interface RunSummaryCardProps {
  run: RunDetail;
  connectionNames: Map<string, string>;
  scheduleName: string | null;
}

/** The server's label for an actor with no users row (`REMOVED_USER_LABEL` in runActors.ts). */
const REMOVED_USER_LABEL = "Removed user";

/** #1217: a firing that started more than this after it was due names its due time too. */
const LATE_FIRING_MS = 60_000;

/**
 * Why the run exists and who started it (Session 10, P11; ux-s10.md §1; Session 11, D3).
 *
 * With the server's `startedBy`: "Started by {name}" for a person ("a removed user" when the
 * account is gone), "Started by {name}, Run now of
 * schedule {schedule}" for a person's Run now of a schedule, "Started by the scheduler: {schedule}
 * at {time} ({zone})" for a firing and "Started by the system" for anything the platform started
 * itself. The name is the server's masked label, never built here. Without it (an older server,
 * or the users read failed) the Session 10 sentences: "Started by schedule {name} at {time}
 * ({timezone})" for a firing, "Started by hand" otherwise.
 *
 * #1217: the firing is an instant, so it reads in the READER's display zone (their preference,
 * else this browser's), named, with its year: "Oct 16, 2026, 09:05 AM IST". Never the schedule's
 * zone: that put the header on another calendar day than the Started field for a reader elsewhere.
 * The "at" instant is the run's first start (`firstStartedAt` after a retry, else `startedAt`, else
 * `createdAt`), the instant the card's Started field reads, or its "First started" line once a
 * retry moved the start (#1291). When the schedule was due (`scheduledFor`) more than a minute earlier, a
 * firing the due scan took late (a runner that was down fires the missed occurrence once on its
 * way back, no catch-up), the due time is its own clause: "..., due at 09:05 AM IST". A firing not
 * yet started reads "Queued by the scheduler", never "Started".
 */
export function startedByLine(
  run: Pick<RunDetail, "kind" | "createdAt"> &
    Partial<
      Pick<RunDetail, "startedBy" | "scheduledFor" | "startedAt" | "firstStartedAt" | "status">
    >,
  scheduleName: string | null,
  viewerZone: string = browserTimeZone(),
): string {
  const named = (iso: string): string => formatInstant(iso, viewerZone, "minutes", { named: true });
  // #1291: a retry restarts `startedAt` (B-30); the header names the firing, so it reads the first
  // start, and the late-firing clause compares that, never the retry's time, with the due time.
  const start = run.firstStartedAt ?? run.startedAt ?? run.createdAt;
  const at = named(start);
  const lateBy =
    run.scheduledFor != null ? Date.parse(start) - Date.parse(run.scheduledFor) : Number.NaN;
  const due = lateBy > LATE_FIRING_MS ? `, due at ${named(run.scheduledFor as string)}` : "";
  const queued =
    run.status !== undefined && isNotStarted({ status: run.status, startedAt: run.startedAt });
  const startedBy = run.startedBy;
  if (startedBy?.kind === "scheduler") {
    // Round 4 (10b): the server's `name` is the schedule's half; the label is never sliced.
    const schedule = scheduleName ?? startedBy.name ?? startedBy.label;
    return queued
      ? `Queued by the scheduler: ${schedule} at ${at}${due}`
      : `Started by the scheduler: ${schedule} at ${at}${due}`;
  }
  if (startedBy?.kind === "user") {
    const who = startedBy.label === REMOVED_USER_LABEL ? "a removed user" : startedBy.label;
    return scheduleName
      ? `Started by ${who}, Run now of schedule ${scheduleName}`
      : `Started by ${who}`;
  }
  if (startedBy?.kind === "system") return "Started by the system";
  if (run.kind === "scheduled") {
    const verb = queued ? "Queued" : "Started";
    return scheduleName
      ? `${verb} by schedule ${scheduleName} at ${at}${due}`
      : `${verb} by a schedule at ${at}${due}`;
  }
  if (run.kind === "event_drain") return "Started by an event";
  if (run.kind === "retry") return "Started by a retry";
  return scheduleName ? `Started by hand, from schedule ${scheduleName}` : "Started by hand";
}

/**
 * Under Rows loaded: nothing with no totals, "not loaded", else how many tables loaded. #1148:
 * while the run moves and some tables read Loaded from the live load (`LIVE_LOAD_ATTEMPT`), the
 * record has no count yet, so it says how many tables loaded and that the rows are counted when
 * the load ends, the same words as those tables' rows, never "not loaded".
 */
function rowsLoadedNote(
  totals: ReturnType<typeof totalsFromRows>,
  tablesLoaded: { loaded: number; noRows: number },
  live: { loaded: number; planned: number } | null,
): string | null {
  if (live && live.loaded > 0 && totals?.rowsLoaded == null) {
    const unit = live.planned === 1 ? "table" : "tables";
    return `${formatCount(live.loaded)} of ${formatCount(live.planned)} ${unit}, counted when the load ends`;
  }
  if (!totals) return null;
  if (totals.rowsLoaded === null) return "not loaded";
  // #2392: the tables this import brought no row for are counted apart ("8 had no rows").
  const { loaded, noRows } = tablesLoaded;
  if (noRows > 0) return tablesLoadedSentence({ loaded, noRows, given: totals.tables });
  const unit = totals.tables === 1 ? "table" : "tables";
  return `${formatCount(loaded)} of ${formatCount(totals.tables)} ${unit}`;
}

/**
 * Under Rows extracted (#1259): "of N expected". The expected total sums the extractor's count
 * only over the tables whose load has reported, so it is shown only when every table the run has
 * figures for has reported, whatever the run's status. Otherwise a run still moving says the
 * total is counted when the load ends, but only while a table can still report; a table whose
 * extract failed or skipped, whose load settled without a count, or a load with no destination
 * will never report, so no note promises one. A finished run with an unreported table shows none.
 */
function expectedNote(
  run: Pick<RunDetail, "status" | "entities" | "steps">,
  totals: ReturnType<typeof totalsFromRows>,
): string | null {
  if (!totals) return null;
  // #1127: a planned table that has not reported is not counted in the totals.
  const rows = run.entities.filter((row) => row.status !== "waiting");
  const covered = rows.length > 0 && rows.every((row) => row.load?.expectedRows != null);
  const moving = !isTerminalRun(run.status);
  if (covered && totals.expectedRows !== null && !(moving && rows.length < run.entities.length)) {
    return `of ${formatCount(totals.expectedRows)} expected`;
  }
  if (!moving || loadHadNoDestination(run.steps)) return null;
  const canStillReport =
    rows.length < run.entities.length ||
    rows.some(
      (row) =>
        row.load?.expectedRows == null &&
        row.status !== "failed" &&
        row.status !== "skipped" &&
        (!row.load || row.load.attempt === LIVE_LOAD_ATTEMPT),
    );
  return canStillReport ? "expected total counted when the load ends" : null;
}

/** The stat row: [label, value, the short line of context under the value]. */
function summaryStats(
  run: RunDetail,
  totals: ReturnType<typeof totalsFromRows>,
  tablesLoaded: { loaded: number; noRows: number },
  zone: string,
  elapsed: number | null,
  live: { loaded: number; planned: number } | null,
): Array<[string, string, string | null]> {
  // #1201: a run still going has run for as long as its start says, which it shows.
  const duration =
    run.durationSeconds === null && elapsed !== null
      ? [formatDuration(elapsed), "so far"]
      : [formatDuration(run.durationSeconds), null];
  return [
    ["Duration", duration[0] as string, duration[1] ?? null],
    ["Rows extracted", totals ? formatCount(totals.rowsExtracted) : "—", expectedNote(run, totals)],
    [
      "Rows loaded",
      formatCount(totals?.rowsLoaded ?? null),
      rowsLoadedNote(totals, tablesLoaded, live),
    ],
    // #1251: the Runs list's word for the trigger, never the stored one.
    ["Kind", RUN_KIND_LABELS[run.kind] ?? run.kind, null],
    // #1291: a retry restarts the run's clock (B-30); the first start stays on the tile.
    [
      "Started",
      startedText(run, zone),
      run.firstStartedAt
        ? `First started ${formatInstant(run.firstStartedAt, zone, "seconds")}`
        : null,
    ],
    ["Finished", run.finishedAt ? formatInstant(run.finishedAt, zone, "seconds") : "Not yet", null],
  ];
}

export function RunSummaryCard({ run, connectionNames, scheduleName }: RunSummaryCardProps) {
  const zone = useDisplayTz();
  const elapsed = useElapsedSeconds(run.status === "running" ? run.startedAt : null);
  const notice = useNotice();
  const presentation = runStatusPresentation(run.status);
  const scopedTables = scopeTableCount(run.scope);
  const deepLink = logAnalyticsUrl(run.runId);

  // Rows per table (O1): the server's totals, or the same maths over the rows from a server
  // that predates them.
  const totals = run.totals ?? totalsFromRows(run.entities);
  // #1127: while the run moves, its tables are counted against the planned ones, as the banner
  // and the Steps line count them. A finished run reads its reported tables alone.
  const rowsLine = rowsLineParts(totals, {
    durationSeconds: run.durationSeconds,
    ...(isTerminalRun(run.status) ? {} : { plannedTables: run.entities.length }),
  });
  // #2392: a table this import brought no row for is not counted as loaded, but apart.
  const noRowsTables = run.entities.filter((entity) => tableNoRows(entity) !== null).length;
  const tablesLoaded = run.entities.filter(
    (entity) => entity.load?.status === "succeeded" && tableNoRows(entity) === null,
  ).length;
  const liveLoaded = isTerminalRun(run.status)
    ? 0
    : run.entities.filter(
        (entity) =>
          entity.load?.attempt === LIVE_LOAD_ATTEMPT && entity.load.status === "succeeded",
      ).length;
  const live = liveLoaded > 0 ? { loaded: tablesLoaded, planned: run.entities.length } : null;
  const connections = run.dataflowIds.map((id) => connectionNames.get(id) ?? id);
  // #1371: a transformation-only run names its dbt job after "Started by", the way the Runs list does.
  const transformOnlyWhat =
    run.type === "transform" || run.type === "import_transform"
      ? transformWhatRan(run.dbtJobNames)
      : null;

  // Session 10 (ux-s10 §3): a copy is confirmed by the button itself, which reads "Copied" for
  // 2 s, rather than by a message somewhere else on the page.
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    if (copied === null) return;
    const handle = setTimeout(() => setCopied(null), DONE_LABEL_MS);
    return () => clearTimeout(handle);
  }, [copied]);

  const copy = async (value: string, what: string, button: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(button);
    } catch {
      // Clipboard access is refused in some browsers and every insecure
      // origin. Saying so beats a button that silently does nothing; the
      // button stays as it was, so the notice is the one thing that says it.
      notice.warning(`${what} could not be copied. Select it and copy it by hand.`);
    }
  };

  const dbtSource = dbtSourceOf(run.steps);
  const stats = summaryStats(
    run,
    totals,
    { loaded: tablesLoaded, noRows: noRowsTables },
    zone,
    elapsed,
    live,
  );

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="run-summary">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SkyBadge appearance="tint" color={presentation.color}>
          {presentation.label}
        </SkyBadge>
        {/* SF14: only when the run says so; an older server's run has no badge. */}
        {run.fullRefresh === true ? (
          <SkyBadge appearance="tint" color="informative" testId="run-full-refresh">
            Full refresh
          </SkyBadge>
        ) : null}
        {/* Product gaps row 2: a run that covered only some tables says how many. */}
        {scopedTables !== null ? (
          <SkyBadge appearance="tint" color="informative" testId="run-scope-tables">
            {tablesWord(scopedTables)}
          </SkyBadge>
        ) : null}
        {run.retryOf ? (
          <SkyLink as={Link} href={absolutePath(`/runs/${run.retryOf}`)} testId="run-retry-of">
            {retryOfLabel(run.retryOf)}
          </SkyLink>
        ) : null}
        <SkyText size={200} color="muted" testId="run-started-by">
          {startedByLine(run, scheduleName, zone)}
          {connections.length > 0 ? ` · ${connections.join(", ")}` : ""}
          {transformOnlyWhat !== null ? ` · ${transformOnlyWhat}` : ""}
        </SkyText>
        {run.status === "cancelled" && run.cancelledBy ? (
          <SkyText size={200} color="muted" testId="run-cancelled-by">
            {`Cancelled by ${cancelledByWording(run.cancelledBy)}`}
          </SkyText>
        ) : null}
        {/* Wraps at phone width, so "Copy the log query" stays inside the card. */}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <SkyText size={200} color="muted" font="monospace">
            {run.runId}
          </SkyText>
          <SkyButton
            appearance="subtle"
            size="small"
            icon={CopyIcon}
            onClick={() => void copy(run.runId, "The run id", "run-id")}
            testId="run-copy-id"
          >
            {copied === "run-id" ? "Copied" : "Copy run id"}
          </SkyButton>
          {deepLink ? (
            <SkyButton
              appearance="subtle"
              size="small"
              icon={LogsIcon}
              onClick={() => window.open(deepLink, "_blank", "noopener,noreferrer")}
              testId="run-logs-link"
            >
              Open logs
            </SkyButton>
          ) : (
            <SkyButton
              appearance="subtle"
              size="small"
              icon={LogsIcon}
              onClick={() =>
                void copy(logAnalyticsQuery(run.runId), "The Log Analytics query", "log-query")
              }
              testId="run-logs-query"
            >
              {copied === "log-query" ? "Copied" : "Copy the log query"}
            </SkyButton>
          )}
        </div>
      </div>

      {totals ? <RowsLine parts={rowsLine} testId="run-rows-line" /> : null}

      <dl className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {stats.map(([label, value, context]) => (
          <div key={label}>
            <dt>
              <SkyText size={200} color="muted" block>
                {label}
              </SkyText>
            </dt>
            <dd>
              <SkyText size={300} weight="semibold" block>
                {value}
              </SkyText>
              {context ? (
                <SkyText size={200} color="muted" block>
                  {context}
                </SkyText>
              ) : null}
            </dd>
          </div>
        ))}
      </dl>

      {/* The two instants above are in the READER's display zone, which is not
          necessarily the schedule's: a run started at 02:00 Eastern reads as
          23:00 to somebody in California, and a page that does not say so is
          wrong for everybody outside the schedule's zone. */}
      {dbtSource ? (
        <SkyText size={200} color="muted" block className="mt-3" testId="run-dbt-source">
          {dbtSourceLine(dbtSource)}
        </SkyText>
      ) : null}

      <SkyText size={200} color="muted" block className="mt-3" testId="run-summary-zone">
        {zoneCaption(zone)}
      </SkyText>
    </div>
  );
}
